import { useEffect, useState, useRef } from "react";
import { Plus, Trash2, Pencil, Bold, Italic, List, Link2, Heading3, Sparkles } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { fmtDate } from "../../lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import MediaPicker from "../../components/MediaPicker";

const EMPTY = { title: "", excerpt: "", content: "", image: "", category: "Genel", tags: "", seo_title: "", seo_description: "", keywords: "", slug: "" };
const inputCls = "w-full h-11 px-4 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30";

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (p) => {
    setF({ title: p.title, excerpt: p.excerpt || "", content: p.content, image: p.image || "", category: p.category || "Genel", tags: (p.tags || []).join(", "), seo_title: p.seo_title || "", seo_description: p.seo_description || "", keywords: (p.keywords || []).join(", "), slug: p.slug || "" });
    setEditId(p.post_id);
    setOpen(true);
  };

  const taRef = useRef(null);
  const wrapSelection = (marker) => {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e, value } = ta;
    const sel = value.slice(s, e) || "metin";
    setF({ ...f, content: value.slice(0, s) + marker + sel + marker + value.slice(e) });
  };
  const insertAtCursor = (snippet) => {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart: s, value } = ta;
    setF({ ...f, content: value.slice(0, s) + snippet + value.slice(s) });
  };

  const seoSuggest = async () => {
    if (!f.title) return toast.error("Önce başlık girin");
    try {
      const { data } = await api.post("/admin/seo/suggest", { title: f.title, content: f.content || f.excerpt, kind: "blog" });
      setF((s) => ({ ...s, seo_title: data.seo_title || s.seo_title, seo_description: data.seo_description || s.seo_description, keywords: (data.keywords || []).join(", "), slug: data.slug || s.slug }));
      toast.success("SEO önerileri oluşturuldu — düzenleyebilirsiniz");
    } catch { toast.error("SEO önerisi alınamadı"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean), keywords: f.keywords ? f.keywords.split(",").map((k) => k.trim()).filter(Boolean) : [] };
    try {
      if (editId) await api.put(`/admin/blog/${editId}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(editId ? "Yazı güncellendi" : "Yazı yayınlandı");
      setOpen(false); setF(EMPTY); setEditId(null); load();
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div data-testid="admin-blog-page">
      <div className="flex items-center justify-between">
        <h1 className="font-serif text-2xl sm:text-3xl text-forest">Blog Yazıları</h1>
        <button data-testid="add-post-button" onClick={() => { setF(EMPTY); setEditId(null); setOpen(true); }} className="flex items-center gap-2 bg-forest hover:bg-forest-dark text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"><Plus className="w-4 h-4" /> Yeni Yazı</button>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200/80 mt-6 overflow-x-auto shadow-sm">
        <table className="w-full text-sm min-w-[560px]">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-stone-400 border-b border-stone-100"><th className="p-4">Başlık</th><th className="p-4">Kategori</th><th className="p-4">Tarih</th><th className="p-4"></th></tr></thead>
          <tbody>
            {posts.map((p) => (
              <tr key={p.post_id} className="border-b border-stone-50">
                <td className="p-4 font-medium">{p.title}</td>
                <td className="p-4 text-stone-600">{p.category}</td>
                <td className="p-4 text-stone-500">{fmtDate(p.published_at)}</td>
                <td className="p-4 text-right"><div className="flex justify-end gap-1">
                  <button data-testid={`edit-post-${p.post_id}`} onClick={() => openEdit(p)} className="p-2 rounded-lg hover:bg-cream-dark text-stone-600"><Pencil className="w-4 h-4" /></button>
                  <button onClick={async () => { if (window.confirm("Yazı silinsin mi?")) { await api.delete(`/admin/blog/${p.post_id}`); load(); } }} className="p-2 rounded-lg hover:bg-red-50 text-terra"><Trash2 className="w-4 h-4" /></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl">
          <DialogHeader><DialogTitle>{editId ? "Yazıyı Düzenle" : "Yeni Blog Yazısı"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <input data-testid="post-title-input" required className={inputCls} placeholder="Başlık *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
            <input className={inputCls} placeholder="Özet" value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} />
            <div className="flex gap-2">
              <input className={`${inputCls} flex-1`} placeholder="Görsel URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} />
              <MediaPicker type="image" testid="blog-media-picker" onSelect={(url) => setF({ ...f, image: url })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="Kategori" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} />
              <input className={inputCls} placeholder="Etiketler (virgülle)" value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} />
            </div>
            <div>
              <div className="flex gap-1.5 mb-2 flex-wrap">
                <button type="button" data-testid="fmt-bold" onClick={() => wrapSelection("**")} className="p-2 rounded-lg border border-stone-300 hover:bg-cream-dark" title="Kalın"><Bold className="w-4 h-4" /></button>
                <button type="button" data-testid="fmt-italic" onClick={() => wrapSelection("*")} className="p-2 rounded-lg border border-stone-300 hover:bg-cream-dark" title="İtalik"><Italic className="w-4 h-4" /></button>
                <button type="button" data-testid="fmt-heading" onClick={() => insertAtCursor("\n## Alt Başlık\n")} className="p-2 rounded-lg border border-stone-300 hover:bg-cream-dark" title="Alt başlık"><Heading3 className="w-4 h-4" /></button>
                <button type="button" data-testid="fmt-list" onClick={() => insertAtCursor("\n- Liste öğesi\n")} className="p-2 rounded-lg border border-stone-300 hover:bg-cream-dark" title="Liste"><List className="w-4 h-4" /></button>
                <button type="button" data-testid="fmt-link" onClick={() => insertAtCursor("[bağlantı metni](https://)")} className="p-2 rounded-lg border border-stone-300 hover:bg-cream-dark" title="Link ekle"><Link2 className="w-4 h-4" /></button>
                <MediaPicker type="image" label="Görsel Ekle" testid="blog-insert-image" onSelect={(url) => insertAtCursor(`\n![görsel](${url})\n`)} />
                <MediaPicker type="video" label="Video Ekle" testid="blog-insert-video" onSelect={(url) => insertAtCursor(`\n[video](${url})\n`)} />
              </div>
              <textarea ref={taRef} data-testid="post-content-input" required rows={8} className="w-full px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="İçerik *" value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} />
            </div>
            <div className="border-t border-stone-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-semibold text-stone-600 uppercase tracking-wider">SEO Alanları</p>
                <button type="button" data-testid="seo-suggest-button" onClick={seoSuggest} className="flex items-center gap-1.5 text-xs font-semibold bg-gold/15 text-gold-dark hover:bg-gold/25 px-3 py-2 rounded-lg transition-colors"><Sparkles className="w-3.5 h-3.5" /> AI ile SEO Öner</button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="post-seo-title" className={inputCls} placeholder="SEO başlığı (max 60 kr.)" value={f.seo_title} onChange={(e) => setF({ ...f, seo_title: e.target.value })} />
                <input data-testid="post-slug" className={`${inputCls} font-mono`} placeholder="url-slug" value={f.slug} onChange={(e) => setF({ ...f, slug: e.target.value })} />
                <input data-testid="post-keywords" className={`${inputCls} col-span-2`} placeholder="Anahtar kelimeler (virgülle)" value={f.keywords} onChange={(e) => setF({ ...f, keywords: e.target.value })} />
                <textarea data-testid="post-seo-description" rows={2} className="col-span-2 px-4 py-3 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30" placeholder="Meta açıklama (max 155 kr.)" value={f.seo_description} onChange={(e) => setF({ ...f, seo_description: e.target.value })} />
              </div>
            </div>
            <button data-testid="post-save-button" className="w-full bg-forest hover:bg-forest-dark text-white font-semibold py-3 rounded-xl transition-colors">{editId ? "Güncelle" : "Yayınla"}</button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
